import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";
const seo = {
  title: "TMS for First Responders | Neuro Wellness TMS",
  description:
    "First responders: learn about TMS therapy, SAINT TMS, and mental health treatment options for depression, anxiety, and PTSD in Coral Springs, FL.",
  canonical: "/tms-for-first-responders",
  keywords:
    "TMS for first responders, first responder mental health treatment, TMS police officers, TMS firefighters, TMS EMS, first responder depression treatment Florida",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: "TMS for First Responders",
    description:
      "Mental health treatment options for first responders including TMS, SAINT, and depression treatment.",
    provider: {
      "@id": "https://neurowellnesstms.com/#organization",
    },
  },
};
const firstResponderLinks = [
  {
    title: "TMS for Police Officers",
    href: "/tms-for-police-officers",
  },
  {
    title: "TMS for Firefighters",
    href: "/tms-for-firefighters",
  },
  {
    title: "TMS for EMS",
    href: "/tms-for-ems",
  },
  {
    title: "TMS for Paramedics",
    href: "/tms-for-paramedics",
  },
  {
    title: "TMS for Sheriff's Deputies",
    href: "/tms-for-sheriffs-deputies",
  },
  {
    title: "Depression Treatment for First Responders",
    href: "/depression-treatment-for-first-responders",
  },
  {
    title: "PTSD and TMS",
    href: "/services/ptsd",
  },
  {
    title: "Anxiety and TMS",
    href: "/services/anxiety",
  },
  {
    title: "First Responder Insurance Verification",
    href: "/first-responder-insurance-verification",
  },
];
export default function TmsForFirstResponders() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="First Responder Resources"
      title="TMS for First Responders"
      subtitle="First responders face high rates of depression, anxiety, and PTSD. This page explains what treatment options may be available and how to find out what your insurance covers."
    >
      <DecisionSection heading="Why this page exists">
        <p>
          First responders — police officers, firefighters, EMTs, paramedics,
          sheriff's deputies — are exposed to traumatic events at rates far
          higher than the general population. Depression, anxiety, and PTSD are
          common, and they're not signs of weakness. They're responses to what
          you've been through.
        </p>
        <p>
          If you've tried medication or therapy and you're still struggling,
          there are other options.
        </p>
      </DecisionSection>

      <DecisionSection heading="Treatment options we offer">
        <DecisionList
          items={[
            "TMS (Transcranial Magnetic Stimulation) — FDA-cleared for depression, non-invasive, outpatient",
            "SAINT accelerated TMS — FDA-cleared, five-day protocol with fMRI targeting",
            "SPRAVATO (esketamine) — FDA-approved for treatment-resistant depression and MDD with suicidal thoughts",
            "Prism for PTSD — EEG neurofeedback for PTSD symptoms",
            "IV ketamine infusions — for depression, anxiety, and PTSD symptoms",
            "Ketamine-assisted psychotherapy (KAP) — combines ketamine with therapy",
            "Psychotherapy — trauma-informed CBT, DBT, EMDR",
            "Medication management — psychiatric evaluation and oversight",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="Insurance considerations">
        <p>
          Many first responders have insurance through their employer — a
          municipal, county, or state government plan. Some have coverage
          through a spouse's plan or a marketplace plan. Coverage for TMS,
          SAINT, and other advanced treatments varies by individual plan.
        </p>
        <p>
          Our team can verify your benefits, handle prior authorization, and
          manage appeals. We'll tell you what your plan covers before you commit
          to anything.
        </p>
      </DecisionSection>

      <DecisionSection heading="Confidentiality">
        <p>
          Your treatment is confidential. We do not share your information with
          your employer, your department, or anyone else without your written
          consent. What happens at Neuro Wellness TMS Centers of America stays
          between you and your care team.
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link
            to="/first-responder-insurance-verification"
            className="text-accent hover:underline"
          >
            start the insurance verification process
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks
        heading="First Responder Resources"
        links={firstResponderLinks}
      />
    </DecisionPageLayout>
  );
}
